import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { GettingStarted } from '@repo/shared-types';
import { fetchApi } from '../lib/api';

/** One key, exported so the six create/attendance hooks invalidate exactly
 *  the entry this query writes (spec
 *  docs/superpowers/specs/2026-08-23-onboarding-v2-design.md §2). */
export const GETTING_STARTED_KEY = ['org', 'getting-started'] as const;

/** Called from every mutation that can flip one of the six flags: create
 *  subject, create/join slot, create student, create enrollment, place into a
 *  slot, mark attendance. The card must vanish on the render after the sixth
 *  flag flips,
 *  and a card that lingers after the teacher just did the thing it asked for
 *  is the single worst failure mode this feature has. */
export function invalidateGettingStarted(qc: QueryClient) {
  qc.invalidateQueries({ queryKey: GETTING_STARTED_KEY });
}

/** True once there is nothing left for the card to report: dismissed, or all
 *  six steps done. Drives the terminal cutover in `useGettingStarted` below —
 *  kept standalone (not inlined) so both the query's options and, if ever
 *  needed, a caller can ask the same question of a payload it already has. */
const isFinished = (d: GettingStarted | undefined) =>
  !!d && (d.dismissedAt !== null || Object.values(d.steps).every(Boolean));

/**
 * The workbench checklist's six booleans + the dismiss stamp.
 *
 * While unfinished: `staleTime: 0` + refetch on focus and on mount,
 * deliberately against the 5-minute `staleTime` its sibling `useOrgSettings`
 * uses — this data changes as a direct result of what the teacher is doing in
 * another tab or another screen, and six cheap counts are worth far less
 * than a stale checklist. The explicit invalidation in the six create hooks
 * is the fast path; these two flags are the safety net for the paths nobody
 * thought of (an import, the AI co-pilot, a second tab).
 *
 * Terminal case: once `isFinished` is true (dismissed, or all six steps
 * done — which is every existing org from day one), `staleTime` goes to
 * `Infinity` and both refetch triggers turn off. Otherwise this query would
 * run forever on every workbench mount/focus for an org with nothing left to
 * report — six counts server-side, one of them an unbounded
 * `sessionParticipant` relation count. The explicit `invalidateGettingStarted`
 * calls still force a refetch on the rare event that un-finishes it (there is
 * none today — dismiss and the six flags only ever move toward "finished" —
 * but nothing here should assume that stays true forever).
 */
export function useGettingStarted() {
  return useQuery<GettingStarted>({
    queryKey: GETTING_STARTED_KEY,
    queryFn: () => fetchApi<GettingStarted>('/org/getting-started'),
    staleTime: (query) => (isFinished(query.state.data) ? Infinity : 0),
    refetchOnWindowFocus: (query) => !isFinished(query.state.data),
    refetchOnMount: (query) => (isFinished(query.state.data) ? false : 'always'),
  });
}

/** The card's "Ẩn" — confirm-less, so the only feedback is the card going
 *  away. `fetchApi` already toasts FORBIDDEN itself (see `maybeToastForbidden`
 *  in `lib/api.ts`), so this only adds a toast for every other failure — a
 *  dismiss that silently failed for a non-permission reason would otherwise
 *  bring the card straight back on the next refetch with no explanation. */
export function useDismissGettingStarted() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      fetchApi<{ dismissedAt: string | null }>('/org/getting-started/dismiss', { method: 'POST' }),
    onSuccess: () => invalidateGettingStarted(qc),
    onError: (e: Error & { code?: string }) => {
      if (e.code !== 'FORBIDDEN') toast.error(e.message || 'Không thể ẩn thẻ');
    },
  });
}
